import type { ReactNode } from 'react';
import { AlertCircle, RotateCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/lib/api';
import { Button } from './button';

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-2xl bg-espresso/[0.06]', className)} />;
}

export function PageSkeleton() {
  return (
    <div className="space-y-5" aria-busy="true" aria-label="Wird geladen">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-40 w-full" />
      <div className="grid gap-4 md:grid-cols-3">
        <Skeleton className="h-28" />
        <Skeleton className="h-28" />
        <Skeleton className="h-28" />
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center rounded-3xl border border-dashed border-line bg-paper/50 px-6 py-10 text-center',
        className,
      )}
    >
      {icon && <div className="mb-4 grid h-12 w-12 place-items-center rounded-full bg-pink-soft text-wine">{icon}</div>}
      <h3 className="font-serif text-xl">{title}</h3>
      {children && <div className="mt-2 max-w-md text-sm leading-6 text-ink-soft">{children}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center rounded-3xl border border-bad/20 bg-bad-soft/50 px-6 py-10 text-center">
      <AlertCircle className="h-6 w-6 text-bad" />
      <p className="mt-3 font-semibold">Das hat nicht geklappt.</p>
      <p className="mt-1 max-w-md text-sm text-ink-soft">{errorMessage(error)}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          <RotateCw className="h-3.5 w-3.5" /> Erneut versuchen
        </Button>
      )}
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  children,
  actions,
}: {
  eyebrow?: string;
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {eyebrow && <div className="text-[11px] font-bold uppercase tracking-label text-wine">{eyebrow}</div>}
        <h1 className="mt-2 font-serif text-4xl leading-[1.05] sm:text-[44px]">{title}</h1>
        {children && <div className="mt-3 max-w-2xl text-[15px] leading-7 text-ink-soft">{children}</div>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

export function Stat({
  label,
  value,
  sub,
  className,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-2xl border border-line bg-paper px-5 py-4', className)}>
      <div className="text-[11px] font-bold uppercase tracking-label text-ink-muted">{label}</div>
      <div className="mt-1.5 font-serif text-3xl leading-none tabular-nums">{value}</div>
      {sub && <div className="mt-1.5 text-xs text-ink-muted">{sub}</div>}
    </div>
  );
}
